import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getStoreLocalData, getUserData } from "../../../utils/localStorage";
import {
  getBrandStoreById,
  postBrandStore,
  putBrandStore,
} from "../../../services/StoreUsers/PostStoreUsers";
import { InputText } from "primereact/inputtext";
import { FloatLabel } from "primereact/floatlabel";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import SuccessModal from "../../SuccessModal";
import AdminMenu from "../../AdminMenu";
import jsQR from "jsqr";
import { Dropdown } from "primereact/dropdown";

/* ---------------------------------------------------------------------- */
/*  Design tokens                                                         */
/* ---------------------------------------------------------------------- */

const COLORS = {
  page: "#F5F7FA",
  card: "#FFFFFF",
  border: "#c1c5ce",
  text: "#1F2937",
  muted: "#6B7280",
  brand: "#1752AA",
  brandSoft: "#EAF1FC",
  danger: "#E5484D",
  onFill: "#1752AA",
};

const CARD_SHADOW =
  "0 1px 2px rgba(16,24,40,0.04), 0 1px 8px rgba(16,24,40,0.06)";

// Each advanced-settings category gets its own icon + accent color so it's
// instantly recognizable at a glance instead of every group looking the same.
const CATEGORY_STYLE = {
  storeManagement: { icon: "pi-building", color: "#1752AA" },
  customerManagement: { icon: "pi-user", color: "#d93434" },
  notifications: { icon: "pi-bell", color: "#8B5CF6" },
  subscriptions: { icon: "pi-star", color: "#F59E0B" },
  orderEdit: { icon: "pi-pencil", color: "#0EA5E9" },
  charges: { icon: "pi-percentage", color: "#16A34A" },
  delivery: { icon: "pi-verified", color: "#06B6D4" },
  packageCarry: { icon: "pi-box", color: "#D97706" },
  rider: { icon: "pi-truck", color: "#6366F1" },
  customerApp: { icon: "pi-mobile", color: "#EC4899" },
};

/* ---------------------------------------------------------------------- */
/*  Small presentational helpers                                          */
/* ---------------------------------------------------------------------- */

// A titled card that groups a set of related fields together.
const Section = ({ title, subtitle, children }) => (
  <div
    className="m-3 p-3 border-round-2xl"
    style={{
      backgroundColor: COLORS.card,
      boxShadow: CARD_SHADOW,
      border: `1px solid ${COLORS.border}`,
    }}
  >
    <div
      className="mb-3"
      style={{
        borderBottom: `1px solid ${COLORS.border}`,
        paddingBottom: "10px",
      }}
    >
      <h3 className="m-0 text-xl font-bold" style={{ color: COLORS.text }}>
        {title}
      </h3>
      {subtitle && (
        <p className="m-0 mt-1 text-sm" style={{ color: COLORS.muted }}>
          {subtitle}
        </p>
      )}
    </div>
    {children}
  </div>
);

// Responsive grid wrapper used for rows of inputs / toggles.
const Grid = ({ minWidth = 360, children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${minWidth}px), 1fr))`,
      gap: "14px",
    }}
  >
    {children}
  </div>
);

// A single on/off feature tile, styled as a switch card rather than a plain
// checkbox row so the on/off state reads at a glance.
const ToggleTile = ({ label, checked, onChange, accent = COLORS.brand }) => (
  <div
    className="px-3 py-2 mt-1 border-round-xl flex justify-content-between align-items-center cursor-pointer transition-colors transition-duration-150"
    style={{
      border: `1px solid ${checked ? accent : COLORS.border}`,
      backgroundColor: checked ? `${accent}0D` : "#FBFCFD",
      minWidth: 0,
    }}
    onClick={() => onChange(!checked)}
  >
    <p
      className="text-base font-medium m-0 mr-3"
      style={{ color: COLORS.text, flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
    >
      {label}
    </p>
    <InputSwitch
      checked={checked}
      onChange={(e) => onChange(e.value)}
      style={{ flexShrink: 0 }}
    />
  </div>
);

// Renders a set of ToggleTiles from a declarative list of
// { key, label, show, onClick } items. Items whose `show` is false are
// skipped, and the group returns null if nothing in it is visible.
const ToggleGrid = ({ items, input, setInput, accent }) => {
  const visibleItems = items.filter((item) => item.show !== false);
  if (visibleItems.length === 0) return null;

  return (
    <Grid>
      {visibleItems.map((item) => (
        <ToggleTile
          key={item.key}
          label={item.label}
          checked={!!input[item.key]}
          accent={accent}
          onChange={
            item.onClick
              ? () => item.onClick()
              : (val) => setInput({ ...input, [item.key]: val })
          }
        />
      ))}
    </Grid>
  );
};

// Accordion header: icon badge + title + a live "X of Y on" count so a
// collapsed category still communicates how many features are active.
const CategoryHeader = ({ title, icon, color, enabledCount, total }) => (
  <div className="flex align-items-center justify-content-between w-full pr-2">
    <div className="flex align-items-center gap-2">
      <span
        className="flex align-items-center justify-content-center border-circle"
        style={{ width: 34, height: 34, backgroundColor: `${color}1A`, color }}
      >
        <i className={`pi ${icon}`} style={{ fontSize: "16px" }}></i>
      </span>
      <span className="text-lg font-semibold" style={{ color: COLORS.text }}>
        {title}
      </span>
    </div>
    <span
      className="text-xs font-semibold px-2 py-1 border-round-lg"
      style={{
        backgroundColor: enabledCount > 0 ? `${color}1A` : "#F1F3F6",
        color: enabledCount > 0 ? color : COLORS.muted,
      }}
    >
      {enabledCount}/{total} on
    </span>
  </div>
);

// A labeled text field, to cut down on the repeated FloatLabel/InputText
// boilerplate for the plain string/number fields at the top of the form.
const Field = ({
  id,
  label,
  value,
  onChange,
  required = false,
  error,
  maxLength,
  keyboardType,
  disabled = false,
}) => (
  <div className="mt-3">
    <FloatLabel>
      <InputText
        id={id}
        value={value}
        onChange={onChange}
        disabled={disabled}
        maxLength={maxLength}
        keyboardType={keyboardType}
        className="w-full text-xl p-2 border-round-xl"
        style={{
          backgroundColor: "#fff",
          border: `1px solid ${COLORS.border}`,
        }}
      />
      <label htmlFor={id} className="text-lg" style={{ color: COLORS.brand }}>
        {label} {required && <span style={{ color: COLORS.danger }}>*</span>}
      </label>
    </FloatLabel>
    {error && (
      <p
        className="text-sm font-normal m-0 mt-1"
        style={{ color: COLORS.danger }}
      >
        {error}
      </p>
    )}
  </div>
);

/* ---------------------------------------------------------------------- */
/*  Main component                                                        */
/* ---------------------------------------------------------------------- */

const StoreManagementCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, upiData } = location.state || {}; // Fallback to prevent undefined errors

  const [storeInfoData, setStoreInfoData] = useState([]);

  const { storeLocalData } = getStoreLocalData();
  const countryCode = storeLocalData?.countryCode;

  const [visible, setVisible] = useState(false);

  const { userProfileRole, brandProfileData } = getUserData();
  const userRole = userProfileRole;
  let brandId = brandProfileData.id;
  const isWareHouseModel = brandProfileData.wareHouseModel;
  let orderEditModel = brandProfileData.orderEditModel;
  let enabledBrandGarmentInspection = brandProfileData.enabledGarmentInspection;
  let enabledPaymentGateway = brandProfileData.enabledPaymentGateway;
  let enabledBrandLocality = brandProfileData.enabledLocality;
  let enabledGarmentAttribute = brandProfileData.enabledGarmentAttribute;
  let enabledCustomerPreferencePrice =
    brandProfileData.enabledCustomerPreferencePrice;
  let enabledBrandOldOrderFeature = brandProfileData.enabledOldOrderFeature;
  let packageCarryForwardBrand = brandProfileData.packageCarryForward;
  let enabledLoyaltyPointBrand = brandProfileData.enabledLoyaltyPoint;
  // let enabledB2BModelBrand = brandProfileData.enabledB2BModel;
  let enabledRackModelBrand = brandProfileData.enabledRackModel;
  let enabledApplySubscriptionWithPackageBrand =
    brandProfileData.enabledApplySubscriptionWithPackage;
  let enabledAutoApplySubscriptionWithPackageBrand =
    brandProfileData.enabledAutoApplySubscriptionWithPackage;
  let brandEnabledSwitchStoreUser = brandProfileData.enabledSwitchStoreUser;
  let brandEnabledPaymentLinkCreationFromCustomerApp =
    brandProfileData.enabledPaymentLinkCreationFromCustomerApp;
  let brandWhatsAppUserPriceOfferLink =
    brandProfileData.whatsAppUserPriceOfferLink;

  const [paymentQrCodeError, setPaymentQrCodeError] = useState("");

  const [loading, setLoading] = useState(false);

  const [isUpdating, setIsUpdating] = useState(id);
  const [modalMenuVisible, setModalMenuVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalStatus, setModalStatus] = useState(false);
  const [modalText, setModalText] = useState("");
  const [isButtonPressed, setIsButtonPressed] = useState(false);

  const fetchStoreUser = (id) => {
    getBrandStoreById(id)
      .then((response) => {
        if (!response.data.error) {
          const Res = response.data;
          setStoreInfoData(Res);
        }
      })
      .catch((err) => {
        console.error("Error:", err);
      });
  };

  const zero = 0;

  useEffect(() => {
    if (isUpdating !== null) {
      fetchStoreUser(isUpdating);
    }
  }, [isUpdating]);

  const rackModelTypeList = [
    {
      name: "AUTO",
      value: "auto"
    },
    {
      name: "MANUAL",
      value: "manual"
    },
    {
      name: "NONE",
      value: "none"
    }
  ]

  const timeSelection = [
    {
      name: "12 PM",
      value: 12,
    },
    {
      name: "1 PM",
      value: 13,
    },
    {
      name: "2 PM",
      value: 14,
    },
    {
      name: "3 PM",
      value: 15,
    },
    {
      name: "4 PM",
      value: 16,
    },
    {
      name: "5 PM",
      value: 17,
    },
    {
      name: "6 PM",
      value: 18,
    },
    {
      name: "7 PM",
      value: 19,
    },
    {
      name: "8 PM",
      value: 20,
    },
    {
      name: "Reset",
      value: 0,
    },
  ];

  const gstTypeList =
    countryCode === "+91"
      ? ["including", "excluding", "composition"]
      : ["including", "excluding"];

  const [input, setInput] = useState({
    address: {
      addressLine1: "",
      addressLine2: "",
      landmark: "",
      latitude: "",
      longitude: "",
      currentLocation: "",
      pin: "",
      city: "",
      state: "",
    },
    gst: "",
    email: "",
    gstNo: "",
    gstType: "excluding",
    company: "",
    premium: false,
    mobileNo: "",
    storeName: "",
    linkShare: false,
    orderTrack: false,
    processTime: "",
    deliveryDateCutoffTime: "",
    rackModelType:"",
    upiAccountId: "",
    upiAccountName: "",
    wareHouseModel: false,
    enabledLocality: false,
    enableCancelOrder: false,
    enabledUPIAccount: false,
    storeInvoiceGenNo: "",
    // enabledB2BModel: false,
    offerPriceEnabled: false,
    orderEditByStore: false,
    orderEditByRider: false,
    enabledReProcess: false,
    // enabledRackModel: false,
    enabledDownloadData: false,
    customerOfferDeal: false,
    storeInvoiceGenName: "",
    enabledStoreLoad: false,
    enabledLightVersion: false,
    packageCarryForward: false,
    enabledLoyaltyPoint: false,
    offerDiscountByStore: false,
    offerDiscountByRider: false,
    // storeB2bChallanGenName: "",
    enabledGSTControl: true,
    riderJobAutoAssigned: false,
    partialPaymentByRider: false,
    partialPaymentByStore: false,
    riderJobHandleEnabled: false,
    enabledMultiPriceList: false,
    enabledOldOrderFeature: false,
    notificationFooterInfo: "",
    whatsAppUserPriceOfferLink: "",
    enableDeliveryDiscount: false,
    enabledSmsNotification: false,
    scaccountHandleEnabled: false,
    enabledBackDate: false,
    enabledEditPayment: false,
    enabledQRDeviceModal: false,
    enabledProfileHandler: true,
    enabledAmountRoundOff: true,
    tagDeliveryDateInterval: zero,
    autoPackageCarryForward: true,
    enabledAutoCustomerId: false,
    enabledSwitchStoreUser: false,
    enabledPreDefinedRemark: false,
    enabledEditIncomeExpense: false,
    enabledEmailNotification: false,
    enableConsolidateInvoice: false,
    enabledCreatePaymentLink: false,
    enabledGarmentInspection: false,
    enableStoreInvoiceGenName: false,
    enableDeliveryVerification: false,
    enableRiderDeliveryDiscount: false,
    enabledAutoSelectAppService: false,
    enabledCustomDiscountCharge: false,
    enabledSingleDiscount: false,
    enabledCustomerRevenueDetail: true,
    enabledCustomerRevenueDetailV2: true,
    enabledRiderPartialBooking: false,
    enabledNewPickupStoreNotified: false,
    enabledAddGarmentServicePrice: false,
    enabledChargeDiscountByService: false,
    enabledAddInternationalCustomer: false,
    enabledLoyaltyPointInSubscription: true,
    enableDeliveryVerificationByRider: false,
    enableDeliveryVerificationByStore: false,
    enableUserAccountDeliveryDiscount: false,
    enabledStoreCustomerBirthdayWishes: true,
    enabledSubscriptionDiscountAutoApply: false,
    enabledApplySubscriptionWithPackage: false,
    enabledAutoApplySubscriptionWithPackage: false,
    enabledSubscriptionDiscountOnDelivery: false,
    enabledOrderBulkEdit: false,
    mandatoryCustomerDetail: false,
    enabledCustomerMobileMasking: false,
    enabledAutoWhatsappNotification: false,
    blockCheckoutInPackageSubscriptionLowBalance: false,
    enabledAutoPackageCalculate: false,
    enabledPaymentLinkCreationFromCustomerApp: false,
    enabledCustomerBillingWithDefaultPrice: false,
  });

  useEffect(() => {
    let savedMobileNo = storeInfoData?.mobileNo;

    if (countryCode && storeInfoData?.mobileNo?.startsWith(countryCode)) {
      savedMobileNo = storeInfoData?.mobileNo?.replace(countryCode, "").trim();
    }

    setInput((prevState) => ({
      ...prevState,
      address: {
        addressLine1: storeInfoData?.address?.addressLine1 || "",
        addressLine2: storeInfoData?.address?.addressLine2 || "",
        landmark: storeInfoData?.address?.landmark || "",
        latitude: storeInfoData?.address?.latitude || "",
        longitude: storeInfoData?.address?.longitude || "",
        currentLocation: storeInfoData?.address?.currentLocation || "",
        pin: storeInfoData?.address?.pin || "",
        city: storeInfoData?.address?.city || "",
        state: storeInfoData?.address?.state || "",
      },
      gst: storeInfoData?.gst?.toString() || "",
      email: storeInfoData?.email || "",
      gstNo: storeInfoData?.gstNo || "",
      gstType: storeInfoData?.gstType || "excluding",
      company: storeInfoData?.company || "",
      premium: storeInfoData?.premium || false,
      mobileNo: savedMobileNo || "",
      storeName: storeInfoData?.storeName || "",
      linkShare: storeInfoData?.linkShare || false,
      orderTrack: storeInfoData?.orderTrack || false,
      processTime: storeInfoData?.processTime?.toString() || "",
      deliveryDateCutoffTime: storeInfoData?.deliveryDateCutoffTime || "",
      rackModelType: storeInfoData?.rackModelType || "",
      enableCancelOrder: storeInfoData?.enableCancelOrder || false,
      wareHouseModel: storeInfoData?.wareHouseModel || false,
      enabledLocality: storeInfoData?.enabledLocality || false,
      storeInvoiceGenNo: storeInfoData?.storeInvoiceGenNo?.toString() || "",
      offerPriceEnabled: storeInfoData?.offerPriceEnabled || false,
      orderEditByStore: storeInfoData?.orderEditByStore || false,
      orderEditByRider: storeInfoData?.orderEditByRider || false,
      // enabledRackModel: storeInfoData?.enabledRackModel || false,
      enabledDownloadData: storeInfoData?.enabledDownloadData || false,
      customerOfferDeal: storeInfoData?.customerOfferDeal || false,
      storeInvoiceGenName: storeInfoData?.storeInvoiceGenName || "",
      enabledStoreLoad: storeInfoData?.enabledStoreLoad || false,
      enabledGSTControl: storeInfoData?.enabledGSTControl || false,
      enabledLightVersion: storeInfoData?.enabledLightVersion || false,
      packageCarryForward: storeInfoData?.packageCarryForward || false,
      enabledLoyaltyPoint: storeInfoData?.enabledLoyaltyPoint || false,
      offerDiscountByStore: storeInfoData?.offerDiscountByStore || false,
      offerDiscountByRider: storeInfoData?.offerDiscountByRider || false,
      riderJobAutoAssigned: storeInfoData?.riderJobAutoAssigned || false,
      riderJobHandleEnabled: storeInfoData?.riderJobHandleEnabled || false,
      enabledMultiPriceList: storeInfoData?.enabledMultiPriceList || false,
      notificationFooterInfo: storeInfoData?.notificationFooterInfo || "",
      whatsAppUserPriceOfferLink:
        storeInfoData?.whatsAppUserPriceOfferLink || "",
      enableDeliveryDiscount: storeInfoData?.enableDeliveryDiscount || false,
      enabledSmsNotification: storeInfoData?.enabledSmsNotification || false,
      scaccountHandleEnabled: storeInfoData?.scaccountHandleEnabled || false,
      enabledBackDate: storeInfoData?.enabledBackDate || false,
      enabledEditPayment: storeInfoData?.enabledEditPayment || false,
      enabledEditIncomeExpense:
        storeInfoData?.enabledEditIncomeExpense || false,
      autoPackageCarryForward: storeInfoData?.autoPackageCarryForward || false,
      enabledAddInternationalCustomer:
        storeInfoData?.enabledAddInternationalCustomer || false,
      enabledAutoCustomerId: storeInfoData?.enabledAutoCustomerId || false,
      enabledNewPickupStoreNotified:
        storeInfoData.enabledNewPickupStoreNotified || false,
      enabledRiderPartialBooking:
        storeInfoData?.enabledRiderPartialBooking || false,
      enabledLoyaltyPointInSubscription:
        storeInfoData?.enabledLoyaltyPointInSubscription || false,
      enabledSwitchStoreUser: storeInfoData?.enabledSwitchStoreUser || false,
      enabledProfileHandler: storeInfoData?.enabledProfileHandler || false,
      enabledAmountRoundOff: storeInfoData?.enabledAmountRoundOff || false,
      enabledQRDeviceModal: storeInfoData?.enabledQRDeviceModal || false,
      enabledAddGarmentServicePrice:
        storeInfoData?.enabledAddGarmentServicePrice || false,
      enableStoreInvoiceGenName:
        storeInfoData?.enableStoreInvoiceGenName || false,
      enabledAutoSelectAppService:
        storeInfoData?.enabledAutoSelectAppService || false,
      enabledEmailNotification:
        storeInfoData?.enabledEmailNotification || false,
      enableConsolidateInvoice:
        storeInfoData?.enableConsolidateInvoice || false,
      enableDeliveryVerification:
        storeInfoData?.enableDeliveryVerification || false,
      enableDeliveryVerificationByRider:
        storeInfoData?.enableDeliveryVerificationByRider || false,
      enableDeliveryVerificationByStore:
        storeInfoData?.enableDeliveryVerificationByStore || false,
      enabledStoreCustomerBirthdayWishes:
        storeInfoData?.enabledStoreCustomerBirthdayWishes || false,

      upiAccountId: storeInfoData?.upiAccountId || "",
      upiAccountName: storeInfoData?.upiAccountName || "",
      enabledUPIAccount: storeInfoData?.enabledUPIAccount || false,
      partialPaymentByRider: storeInfoData?.partialPaymentByRider || false,
      enabledCreatePaymentLink:
        storeInfoData?.enabledCreatePaymentLink || false,
      enabledGarmentInspection:
        storeInfoData?.enabledGarmentInspection || false,
      enableUserAccountDeliveryDiscount:
        storeInfoData?.enableUserAccountDeliveryDiscount || false,
      partialPaymentByStore: storeInfoData?.partialPaymentByStore || false,
      enableRiderDeliveryDiscount:
        storeInfoData?.enableRiderDeliveryDiscount || false,

      enabledReProcess: storeInfoData?.enabledReProcess || false,
      tagDeliveryDateInterval:
        storeInfoData?.tagDeliveryDateInterval?.toString() || zero.toString(),
      enabledPreDefinedRemark: storeInfoData?.enabledPreDefinedRemark || false,
      enabledCustomerRevenueDetail:
        storeInfoData?.enabledCustomerRevenueDetail || false,
      enabledCustomerRevenueDetailV2:
        storeInfoData?.enabledCustomerRevenueDetailV2 || false,
      enabledChargeDiscountByService:
        storeInfoData?.enabledChargeDiscountByService || false,
      enabledCustomDiscountCharge:
        storeInfoData?.enabledCustomDiscountCharge || false,
      enabledSingleDiscount:
        storeInfoData?.enabledSingleDiscount || false,
      enabledSubscriptionDiscountOnDelivery:
        storeInfoData?.enabledSubscriptionDiscountOnDelivery || false,
      enabledSubscriptionDiscountAutoApply:
        storeInfoData?.enabledSubscriptionDiscountAutoApply || false,
      enabledOldOrderFeature: storeInfoData?.enabledOldOrderFeature || false,
      enabledApplySubscriptionWithPackage:
        storeInfoData?.enabledApplySubscriptionWithPackage || false,
      enabledAutoApplySubscriptionWithPackage:
        storeInfoData?.enabledAutoApplySubscriptionWithPackage || false,

      enabledB2BModel: storeInfoData?.enabledB2BModel || false,

      enabledOrderBulkEdit: storeInfoData?.enabledOrderBulkEdit || false,
      mandatoryCustomerDetail: storeInfoData?.mandatoryCustomerDetail || false,
      enabledCustomerMobileMasking:
        storeInfoData?.enabledCustomerMobileMasking || false,
      blockCheckoutInPackageSubscriptionLowBalance:
        storeInfoData?.blockCheckoutInPackageSubscriptionLowBalance || false,
      enabledAutoWhatsappNotification:
        storeInfoData?.enabledAutoWhatsappNotification || false,
      enabledAutoPackageCalculate:
        storeInfoData?.enabledAutoPackageCalculate || false,
      enabledPaymentLinkCreationFromCustomerApp:
        storeInfoData?.enabledPaymentLinkCreationFromCustomerApp || false,
      enabledCustomerBillingWithDefaultPrice:
        storeInfoData?.enabledCustomerBillingWithDefaultPrice || false,
    }));
  }, [storeInfoData]);

  const [storeNameError, setStoreNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [mobileNoError, setMobileNoError] = useState("");
  const [tagDeliveryDateIntervalError, setTagDeliveryDateIntervalError] =
    useState("");
  const [storeInvoiceGenNameError, setStoreInvoiceGenNameError] = useState("");
  const [storeInvoiceGenNoError, setStoreInvoiceGenNoError] = useState("");
  const [gstNoError, setGstNoError] = useState("");
  const [gstError, setGstError] = useState("");
  const [addressLineError, setAddressLineError] = useState("");
  const [stateError, setStateError] = useState("");
  const [pincodeError, setPincodeError] = useState("");

  const handleGstType = (value) => {
    const updatedInput = {
      ...input,
      gstType: value,
      gst: "",
    };

    if (value === "composition") {
      updatedInput.gst = 0;
    }

    if (updatedInput.gst === 0) {
      setGstError("");
    } else {
      setGstError("Please enter a valid " + storeLocalData?.taxType);
    }

    setInput(updatedInput);
  };

  const handleDeliveryDateCutoffTime = (value) => {
    const updatedInput = {
      ...input,
      deliveryDateCutoffTime: value,
    };
    setInput(updatedInput);
  };

  const handleRackModelType = (value) => {
    const updatedInput = {
      ...input,
      rackModelType: value,
    };
    setInput(updatedInput);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const image = new Image();
        image.src = e.target.result;
        image.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = image.width;
          canvas.height = image.height;
          ctx.drawImage(image, 0, 0, image.width, image.height);

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const qrCode = jsQR(
            imageData.data,
            imageData.width,
            imageData.height,
          );

          if (qrCode) {
            const queryString = qrCode.data.split("?")[1]; // Get the part after '?'
            if (!queryString) {
              setPaymentQrCodeError(
                "Invalid Upi Qr Code Please Go Back And Come Again",
              );
              return;
            }

            // Manually parse query parameters
            const params = {};
            queryString.split("&").forEach((param) => {
              const [key, value] = param.split("=");
              params[key] = decodeURIComponent(value || "");
            });

            const upiData = {
              upiId: params.pa || "",
              upiUserName: params.pn || "",
            };

            setInput({
              ...input,
              upiAccountName: upiData.upiUserName,
              upiAccountId: upiData.upiId,
            });
          } else {
            setPaymentQrCodeError(
              "No Qr Code Found In Image Or Qr Code Image is Clear",
            );
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const [selectedItem, setSelectedItem] = useState(null);

  const handleItemPress = () => {
    setSelectedItem((prevSelectedItem) => !prevSelectedItem);
  };

  const handleMobileNumberChange = (number) => {
    // Perform input validation to allow only Indian mobile numbers

    let isValidPartial = false;
    let isValidFull = false;
    let requiredLength = 10;

    // Validation based on country code
    if (countryCode === "+91") {
      // India: starts with 5-9 and max 10 digits
      isValidPartial = /^[5-9]\d{0,9}$/.test(number) || number === "";
      isValidFull = /^[5-9]\d{9}$/.test(number);
      requiredLength = 10;
    } else if (countryCode === "+971") {
      // UAE: starts with 5 and max 9 digits
      isValidPartial = /^5\d{0,8}$/.test(number) || number === "";
      isValidFull = /^5\d{8}$/.test(number);
      requiredLength = 9;
    }

    if (isValidPartial) {
      if (number.length === requiredLength) {
        setMobileNoError("");
      } else {
        setMobileNoError(
          `Please enter a ${requiredLength}-digit mobile number`,
        );
      }

      setInput({
        ...input,
        mobileNo: number,
      });
    } else {
      setMobileNoError("Please enter a valid mobile number");
    }
  };

  const handleTagDeliveryDateInterval = (number) => {
    if (number.trim() === "") {
      setTagDeliveryDateIntervalError(
        "Tag Delivery Date Interval cannot be empty",
      );
      setIsButtonPressed(true);
    } else if (parseInt(number) >= parseInt(input.processTime)) {
      setTagDeliveryDateIntervalError(
        "Tag Delivery Date Interval must be less than process time",
      );
      setIsButtonPressed(true);
    } else {
      setTagDeliveryDateIntervalError(""); // Clear the error if validation passes
      setIsButtonPressed(false);
    }

    setInput({
      ...input,
      tagDeliveryDateInterval: number,
    });
  };

  const handleProcessTimeChange = (newProcessTime) => {
    setInput((prevState) => ({
      ...prevState,
      processTime: newProcessTime,
      tagDeliveryDateInterval: "0", // Reset tagDeliveryDateInterval to '0'
    }));
    setTagDeliveryDateIntervalError(""); // Clear any existing error
    setIsButtonPressed(false);
  };

  const handleDeliveryVerification = () => {
    if (!input.enableDeliveryVerification) {
      setInput({
        ...input,
        enableDeliveryVerification: !input.enableDeliveryVerification,
        enableDeliveryVerificationByRider: false,
        enableDeliveryVerificationByStore: false,
      });
    } else {
      setInput({
        ...input,
        enableDeliveryVerification: !input.enableDeliveryVerification,
      });
    }
  };

  const validateGST = (gst) => {
    let gstRegex = false;

    if (countryCode === "+91") {
      // India: starts with 5-9 and 10 digits
      gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    } else if (countryCode === "+971") {
      // UAE: 15 digits
      gstRegex = /^\d{15}$/;
    } else {
      // Fallback validation (digits only, length 6-15)
      gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    }

    return gstRegex.test(gst);
  };

  const isValidEmail = (email) => {
    // Regular expression for basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = () => {
    // Reset all previous errors
    setStoreNameError("");
    setEmailError("");
    setMobileNoError("");
    setStoreInvoiceGenNameError("");
    setStoreInvoiceGenNoError("");

    let isValid = true;
    if (!input.storeName) {
      setStoreNameError("Store Name is Required");
      isValid = false;
    }
    if (!input.email) {
      setEmailError("Email is Required");
      isValid = false;
    }
    if (!input.mobileNo) {
      setMobileNoError("Mobile Number is Required");
      isValid = false;
    }
    if (!input.storeInvoiceGenName) {
      setStoreInvoiceGenNameError("Invoice Gen Name is Required");
      isValid = false;
    }
    if (!input.storeInvoiceGenNo) {
      setStoreInvoiceGenNoError("Invoice Gen No. is required");
      isValid = false;
    } else if (input.storeInvoiceGenNo <= 0) {
      setStoreInvoiceGenNoError("No. must be grater then 0");
      isValid = false;
    }

    if (!input.address.addressLine1) {
      setAddressLineError("Address Line 1 is Required");
      isValid = false;
    }

    if (!input.address.state) {
      setStateError("State Name is Required");
      isValid = false;
    }

    if (!input.address.pin) {
      setPincodeError("Pin Code is Required");
      isValid = false;
    }

    if (gstNoError) {
      setGstNoError(storeLocalData?.taxType + " No is Required");
      isValid = false;
    }

    if (gstError) {
      setGstError(storeLocalData?.taxType + " is Required");
      isValid = false;
    }

    if (isValid) {
      // Logic for creating a new pickup request
      if (!isButtonPressed) {
        // Set the state to indicate that the button has been pressed
        setIsButtonPressed(true);

        const data = {
          ...input,
          mobileNo: countryCode + input.mobileNo,
          brandId: brandId,
        };

        const UpdateData = {
          ...input,
          mobileNo: countryCode + input.mobileNo,
          brandId: brandId,
          id: storeInfoData?.id,
          status: storeInfoData.status,
        };

        // Call the service function for creating pickup request
        if (!isUpdating) {
          postBrandStore(brandId, data)
            .then((response) => {
              if (!response.data.error) {
                const Res = response.data;
                if (Res) {
                  const message = Res.message;
                  const status = Res.status;
                  if (status === true) {
                    setModalVisible(true);
                    setModalStatus("success");
                    setModalText(message);
                    setTimeout(() => {
                      navigate("/StoreManagementList");
                    }, 1500);
                    setIsButtonPressed(false);
                  } else {
                    setModalVisible(true);
                    setModalStatus("not Success");
                    setModalText(message);
                    setIsButtonPressed(false);
                  }
                }
              }
            })
            .catch((err) => {
              console.error("Error:", err);
            });
        } else {
          putBrandStore(brandId, UpdateData)
            .then((response) => {
              if (!response.data.error) {
                const Res = response.data;
                if (Res) {
                  const message = Res.message;
                  const status = Res.status;
                  if (status === true) {
                    setModalVisible(true);
                    setModalStatus("success");
                    setModalText(message);
                    setTimeout(() => {
                      navigate("/StoreManagementList");
                    }, 1500);
                    setIsButtonPressed(false);
                  } else {
                    setModalVisible(true);
                    setModalStatus("not Success");
                    setModalText(message);
                    setIsButtonPressed(false);
                  }
                }
              }
            })
            .catch((err) => {
              console.error("Error:", err);
            });
        }
      }
    } else {
      setIsButtonPressed(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /*  Declarative toggle groups — this is where the previously scattered    */
  /*  Checkbox blocks are segregated by subject into named sub-sections.    */
  /* ---------------------------------------------------------------------- */

  const generalStoreToggles = [
    { key: "partialPaymentByStore", label: "Partial Payment By Store" },
    { key: "enabledUPIAccount", label: "Enable UPI Account" },
    { key: "offerPriceEnabled", label: "Offer Price" },
    {
      key: "enableStoreInvoiceGenName",
      label: "Enable Store Invoice Gen Name",
    },
    { key: "enabledReProcess", label: "Enable Re-Process" },
    { key: "enabledAutoSelectAppService", label: "Auto Select App Service" },
    { key: "scaccountHandleEnabled", label: "SCAccount Handle" },
    { key: "enabledStoreLoad", label: "Store Load" },
    { key: "enabledLightVersion", label: "Light Version" },
    { key: "enabledMultiPriceList", label: "Enable Multi Tariff List" },
    {
      key: "enabledAddGarmentServicePrice",
      label: "Add Garment Service Price Using Store",
    },
    { key: "enableConsolidateInvoice", label: "Consolidate Invoice" },
    { key: "enableCancelOrder", label: "Cancel Order" },
    { key: "linkShare", label: "Link Share" },
    { key: "premium", label: "Premium" },
    {
      key: "enabledNewPickupStoreNotified",
      label: "New Pickup Store Notified",
    },
    {
      key: "enabledGSTControl",
      label: `Enable ${storeLocalData?.taxType || "Tax"} Control`,
    },
    { key: "enabledProfileHandler", label: "Enable Profile Handler" },
    { key: "enabledAmountRoundOff", label: "Enable Amount Round Off" },
    { key: "enabledQRDeviceModal", label: "Enable QR Device Modal" },
    { key: "enabledOrderBulkEdit", label: "Enable Order Bulk Edit" },
    { key: "enabledBackDate", label: "Enable Back Date" },
    { key: "enabledEditIncomeExpense", label: "Edit Income Expense" },
    { key: "enabledAutoPackageCalculate", label: "Auto Package Calculate" },
    {
      key: "enabledPreDefinedRemark",
      label: "Pre Defined Remark",
      show: enabledGarmentAttribute,
    },
    {
      key: "enabledLocality",
      label: "Enable Locality",
      show: enabledBrandLocality,
    },
    {
      key: "wareHouseModel",
      label: "Store-Warehouse Model",
      show: brandProfileData.wareHouseModel,
    },
    { key: "orderTrack", label: "Order Track", show: orderEditModel },
    {
      key: "enabledGarmentInspection",
      label: "Garment Inspection",
      show: enabledBrandGarmentInspection,
    },
    {
      key: "enabledOldOrderFeature",
      label: "Old Order Feature",
      show: enabledBrandOldOrderFeature,
    },
    {
      key: "enabledCreatePaymentLink",
      label: "Create Payment Link",
      show: enabledPaymentGateway,
    },
    {
      key: "enabledLoyaltyPoint",
      label: "Loyalty Point",
      show: enabledLoyaltyPointBrand,
    },
    // {
    //   key: "enabledRackModel",
    //   label: "Enable Rack Model",
    //   show: enabledRackModelBrand,
    // },
    {
      key: "enabledSwitchStoreUser",
      label: "Enable Switch Store User",
      show: brandEnabledSwitchStoreUser,
    },

    {key: "enabledDownloadData", label: "Enable Download Store Data"},
  ];

  const customerToggles = [
    { key: "enabledCustomerRevenueDetail", label: "Customer Revenue Detail" },
    {
      key: "enabledCustomerRevenueDetailV2",
      label: "Customer Revenue Detail V2",
    },
    {
      key: "enabledStoreCustomerBirthdayWishes",
      label: "Store Customer Birthday Wishes",
    },
    { key: "mandatoryCustomerDetail", label: "Mandatory Customer Detail" },
    {
      key: "enabledCustomerMobileMasking",
      label: "Enable Customer Mobile Masking",
    },
    {
      key: "enabledAddInternationalCustomer",
      label: "Add International Customer",
    },
    { key: "enabledAutoCustomerId", label: "Auto Customer Id" },
    {
      key: "enabledCustomerBillingWithDefaultPrice",
      label: "Customer Billing With Default Price",
    },
    {
      key: "customerOfferDeal",
      label: "Customer Offer Deal",
      show: enabledCustomerPreferencePrice,
    },
  ];

  const notificationToggles = [
    { key: "enabledEmailNotification", label: "Email Notification" },
    { key: "enabledSmsNotification", label: "SMS Notification" },
    {
      key: "enabledAutoWhatsappNotification",
      label: "Enable Auto Whatsapp Notification",
    },
  ];

  const subscriptionToggles = [
    {
      key: "enabledSubscriptionDiscountOnDelivery",
      label: "Subscription Discount On Delivery",
    },
    {
      key: "enabledSubscriptionDiscountAutoApply",
      label: "Subscription Discount Auto Apply",
    },
    {
      key: "blockCheckoutInPackageSubscriptionLowBalance",
      label: "Block Checkout In Package Subscription Low Balance",
    },
    {
      key: "enabledApplySubscriptionWithPackage",
      label: "Apply Subscription With Package",
      show: enabledApplySubscriptionWithPackageBrand,
    },
    {
      key: "enabledAutoApplySubscriptionWithPackage",
      label: "Auto Apply Subscription With Package",
      show: enabledAutoApplySubscriptionWithPackageBrand,
    },
    {
      key: "enabledLoyaltyPointInSubscription",
      label: "Loyalty Point in Subscription",
      show: enabledLoyaltyPointBrand,
    },
    {
      key: "packageCarryForward",
      label: "Package Carry Forward",
      show: packageCarryForwardBrand,
    },
    {
      key: "autoPackageCarryForward",
      label: "Auto Package Carry Forward",
      show: packageCarryForwardBrand,
    },
  ];

  const orderEditToggles = [
    {
      key: "orderEditByStore",
      label: "Order Edit By Store",
      show: orderEditModel,
    },
    {
      key: "orderEditByRider",
      label: "Order Edit By Rider",
      show: orderEditModel,
    },
    { key: "enabledEditPayment", label: "Edit Payment" },
  ];

  const chargesDiscountsToggles = [
    { key: "offerDiscountByRider", label: "Offer Discount By Rider" },
    { key: "enableDeliveryDiscount", label: "Delivery Discount" },
    {
      key: "enableUserAccountDeliveryDiscount",
      label: "User Account Delivery Discount",
    },
    { key: "offerDiscountByStore", label: "Offer Discount By Store" },
    { key: "enableRiderDeliveryDiscount", label: "Rider Delivery Discount" },
    { key: "enabledChargeDiscountByService", label: "Custom Charges" },
    { key: "enabledCustomDiscountCharge", label: "Custom Discount" },
    { key: "enabledSingleDiscount", label: "Single Discount" },
  ];

  const deliveryVerificationToggles = [
    {
      key: "enableDeliveryVerification",
      label: "Delivery Verification",
      onClick: handleDeliveryVerification,
    },
    {
      key: "enableDeliveryVerificationByStore",
      label: "Delivery Verification By Store",
      show: input.enableDeliveryVerification,
    },
    {
      key: "enableDeliveryVerificationByRider",
      label: "Delivery Verification By Rider",
      show: input.enableDeliveryVerification,
    },
  ];

  const riderToggles = [
    { key: "riderJobAutoAssigned", label: "Rider Job Auto Assigned" },
    { key: "riderJobHandleEnabled", label: "Rider Job Handle" },
    { key: "enabledRiderPartialBooking", label: "Rider Partial Booking" },
    { key: "partialPaymentByRider", label: "Partial Payment By Rider" },
  ];

  const customerAppToggles = [
    {
      key: "enabledPaymentLinkCreationFromCustomerApp",
      label: "Payment Link Creation From Customer App",
      show: brandEnabledPaymentLinkCreationFromCustomerApp,
    },
  ];

  const requiredChecklist = [
    { label: "Store Name", done: !!input.storeName },
    { label: "Email", done: isValidEmail(input.email) },
    { label: "Mobile Number", done: !!input.mobileNo },
    { label: "Invoice Gen Name", done: !!input.storeInvoiceGenName },
    {
      label: "Invoice Gen No.",
      done: !!input.storeInvoiceGenNo && Number(input.storeInvoiceGenNo) > 0,
    },
    { label: "Address Line 1", done: !!input.address.addressLine1 },
    { label: "State", done: !!input.address.state },
    { label: "Pin Code", done: !!input.address.pin },
  ];

  return (
    <div className="flex-1" style={{ backgroundColor: COLORS.page }}>
      {/* ---------- Header ---------- */}
      <div
        className="h-3rem surface-0 sticky z-2 mb-3 top-0 flex justify-content-center align-items-center"
        style={{
          "--surface-0": "#1752AA",
        }}
      >
        <div className="flex-1 flex ml-3 mr-3">
          <div className="flex-1 flex ">
            <div className="ml-3 flex justify-content-center align-items-center">
              <div className="h-1rem w-1rem border-circle flex justify-content-center align-items-center">
                <i
                  className="pi pi-angle-left"
                  onClick={() => navigate("/StoreManagementList")}
                  style={{ fontSize: "27px", cursor: "pointer", color: "#fff" }}
                ></i>
              </div>
            </div>
            <div className="flex-1 ml-5">
              <p className="text-2xl text-white font-bold uppercase">
                Store {id ? "Edit" : "Create"}
              </p>
            </div>
          </div>

          <div
            className="flex justify-content-center align-items-center cursor-pointer"
            onClick={() => setVisible(true)}
          >
            <i
              className="pi pi-bars"
              style={{ fontSize: "27px", marginRight: 30, color: "#fff" }}
            ></i>
          </div>
        </div>
      </div>

      {/* ---------- Store Details ---------- */}
      <Section title="Store Details">
        <Grid>
          <Field
            id="storeName"
            label="Store Name"
            required
            value={input.storeName}
            error={storeNameError}
            onChange={(e) => {
              setInput({ ...input, storeName: e.target.value });
              setStoreNameError("");
            }}
          />
          <Field
            id="company"
            label="Company"
            value={input.company}
            onChange={(e) => setInput({ ...input, company: e.target.value })}
          />
          <Field
            id="email"
            label="Email Id"
            required
            value={input.email}
            error={emailError}
            onChange={(e) => {
              setInput({ ...input, email: e.target.value });
              setEmailError(
                isValidEmail(e.target.value)
                  ? ""
                  : "Please enter a valid email address",
              );
            }}
          />
          <Field
            id="mobileNo"
            label="Mobile Number"
            required
            value={input.mobileNo}
            error={mobileNoError}
            keyboardType="numeric"
            maxLength={
              countryCode === "+971"
                ? countryCode.length + 9
                : countryCode.length + 10
            }
            onChange={(e) => handleMobileNumberChange(e.target.value)}
          />
          <Field
            id="processTime"
            label="Process Time"
            value={input.processTime}
            keyboardType="numeric"
            maxLength={2}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setInput({ ...input, processTime: value });
              }
            }}
          />
          <Field
            id="tagDeliveryDateInterval"
            label="Tag Delivery Date Interval"
            value={input.tagDeliveryDateInterval}
            onChange={(e) =>
              setInput({ ...input, tagDeliveryDateInterval: e.target.value })
            }
          />

          <div className="mt-3">
            <FloatLabel>
              <Dropdown
                value={input.deliveryDateCutoffTime}
                onChange={(e) => handleDeliveryDateCutoffTime(e.value)}
                options={timeSelection}
                optionLabel="name"
                placeholder={"Delivery Date Cutoff Time"}
                className="w-full bg-white-alpha-20 border-round-lg p-0 text-lg text-primary-50"
              />
              <label className="text-lg" style={{ color: COLORS.brand }}>
                Delivery Date Cutoff Time
              </label>
            </FloatLabel>
          </div>
          {enabledRackModelBrand && <div className="mt-3">
            <FloatLabel>
              <Dropdown
                value={input.rackModelType}
                onChange={(e) => handleRackModelType(e.value)}
                options={rackModelTypeList}
                optionLabel="name"
                placeholder={"Rack Model Type"}
                className="w-full bg-white-alpha-20 border-round-lg p-0 text-lg text-primary-50"
              />
              <label className="text-lg" style={{ color: COLORS.brand }}>
                Rack Model Type
              </label>
            </FloatLabel>
          </div>}
          
        </Grid>
      </Section>

      {/* ---------- Invoicing & Tax ---------- */}
      <Section title="Invoicing & Tax">
        <Grid>
          <Field
            id="InvoiceGenName"
            label="Invoice Gen Name"
            required
            value={input.storeInvoiceGenName}
            error={storeInvoiceGenNameError}
            onChange={(e) => {
              setInput({ ...input, storeInvoiceGenName: e.target.value });
              setStoreInvoiceGenNameError("");
            }}
          />
          <Field
            id="InvoiceGenNo"
            label="Invoice Gen Number"
            required
            value={input.storeInvoiceGenNo}
            error={storeInvoiceGenNoError}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setInput({ ...input, storeInvoiceGenNo: value });
                setStoreInvoiceGenNoError("");
              }
            }}
          />
          <div>
            <Field
              id="gstNo"
              label={`${storeLocalData?.taxType} Number`}
              value={input.gstNo}
              maxLength={15}
              error={gstNoError}
              onChange={(e) => {
                const value = e.target.value;
                const isValid = validateGST(value);

                setInput((prev) => ({ ...prev, gstNo: value, gst: "" }));

                if (!isValid) {
                  setGstNoError(
                    value.length > 1
                      ? "Please enter a valid " +
                          storeLocalData?.taxType +
                          " No."
                      : "",
                  );
                  setGstError("");
                } else {
                  setGstNoError("");
                  setGstError(
                    "Please enter a valid " + storeLocalData?.taxType,
                  );
                }
              }}
            />
          </div>
          <div>
            <Field
              id="gst"
              label={`${storeLocalData?.taxType} %`}
              value={input.gst}
              maxLength={2}
              error={gstError}
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d*$/.test(value)) {
                  setInput({ ...input, gst: value });
                }
                setGstError(value ? "" : "Please enter correct Value");
              }}
            />
          </div>
          {input.gstNo && (
            <div className="mt-3">
              <FloatLabel>
                <Dropdown
                  value={input.gstType}
                  onChange={(e) => handleGstType(e.value)}
                  options={gstTypeList}
                  optionLabel="name"
                  placeholder={`${storeLocalData?.taxType} Type`}
                  className="w-full bg-white-alpha-20 border-round-lg p-0 text-lg text-primary-50"
                />
                <label className="text-lg" style={{ color: "blue" }}>
                  {storeLocalData?.taxType} Type
                </label>
              </FloatLabel>
            </div>
          )}
        </Grid>
      </Section>

      {/* ---------- Payment / UPI ---------- */}
      <Section title="Payment & UPI">
        <Grid>
          <Field
            id="upiAccountName"
            label="Upi Account Name"
            value={input.upiAccountName}
            disabled
            onChange={(e) =>
              setInput({ ...input, upiAccountName: e.target.value })
            }
          />
          <Field
            id="upiAccountId"
            label="Upi Account Id"
            value={input.upiAccountId}
            disabled
            onChange={(e) =>
              setInput({ ...input, upiAccountId: e.target.value })
            }
          />
          <div className="flex align-items-center gap-2">
            <Checkbox checked={true} color={COLORS.brand} size={20} />
            <p
              className="text-sm font-medium underline cursor-pointer m-0"
              onClick={() =>
                navigate("/UPIDisclaimer", {
                  state: { id: id, storeInfoData: storeInfoData },
                })
              }
              style={{ color: COLORS.brand }}
            >
              Please Click to Add Payment QR
            </p>
          </div>
        </Grid>

        {/* <div
          className="mt-3 p-3 border-round-xl"
          style={{ border: `1px dashed ${COLORS.brand}55`, backgroundColor: COLORS.brandSoft }}
        >
          <div className="flex flex-row justify-content-between align-items-center flex-wrap gap-2">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-qrcode" style={{ color: COLORS.brand, fontSize: "18px" }}></i>
              <h4 className="m-0" style={{ color: COLORS.text }}>
                Upload Your Payment QrCode Image Below
              </h4>
            </div>
            <div className="flex align-items-center gap-2">
              <Checkbox checked={true} color={COLORS.brand} size={20} />
              <p
                className="text-sm font-medium underline cursor-pointer m-0"
                onClick={() => navigate("/UPIDisclaimer", { state: { id: id } })}
                style={{ color: COLORS.brand }}
              >
                Disclaimer For Upi Id Storage & Liability
              </p>
            </div>
          </div>
          <input type="file" accept="image/*" onChange={handleImageUpload} className="mt-3" />
          {paymentQrCodeError && (
            <p className="text-sm font-normal mt-2" style={{ color: COLORS.danger }}>
              {paymentQrCodeError}
            </p>
          )}
        </div> */}
      </Section>

      {/* ---------- Address ---------- */}
      <Section title="Address">
        <Grid>
          <Field
            id="addressLine1"
            label="Address Line1"
            required
            value={input.address.addressLine1}
            error={addressLineError}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, addressLine1: e.target.value },
              })
            }
          />
          <Field
            id="addressLine2"
            label="Address Line2"
            value={input.address.addressLine2}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, addressLine2: e.target.value },
              })
            }
          />
          <Field
            id="city"
            label="City"
            value={input.address.city}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, city: e.target.value },
              })
            }
          />
          <Field
            id="state"
            label="State"
            required
            value={input.address.state}
            error={stateError}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, state: e.target.value },
              })
            }
          />
          <Field
            id="landMark"
            label="LandMark"
            value={input.address.landmark}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, landmark: e.target.value },
              })
            }
          />
          <Field
            id="pin"
            label="Pin Code"
            value={input.address.pin}
            error={pincodeError}
            keyboardType="numeric"
            maxLength={6}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*$/.test(value)) {
                setInput({
                  ...input,
                  address: { ...input.address, pin: value },
                });
              }
            }}
          />
          <Field
            id="latitude"
            label="Latitude"
            value={input.address.latitude}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, latitude: e.target.value },
              })
            }
          />
          <Field
            id="longitude"
            label="Longitude"
            keyboardType="numeric"
            value={input.address.longitude}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, longitude: e.target.value },
              })
            }
          />
          <Field
            id="currentLocation"
            label="Current Location"
            keyboardType="numeric"
            value={input.address.currentLocation}
            onChange={(e) =>
              setInput({
                ...input,
                address: { ...input.address, currentLocation: e.target.value },
              })
            }
          />
        </Grid>
      </Section>

      {/* ---------- Notifications copy ---------- */}
      <Section title="Notification Messages">
        <Grid>
          <Field
            id="notificationFooterInfo"
            label="Notification Note Message"
            value={input.notificationFooterInfo}
            onChange={(e) =>
              setInput({ ...input, notificationFooterInfo: e.target.value })
            }
          />

          <Field
            id="whatsAppUserPriceOfferLink"
            label="WhatsApp User Price Offer Link"
            value={input.whatsAppUserPriceOfferLink}
            onChange={(e) =>
              setInput({ ...input, whatsAppUserPriceOfferLink: e.target.value })
            }
          />
        </Grid>
      </Section>

      {/* ---------- Additional Information (collapsible, grouped) ---------- */}
      <div
        className="m-3 border-round-2xl cursor-pointer"
        style={{
          backgroundColor: COLORS.card,
          boxShadow: CARD_SHADOW,
          border: `1px solid ${COLORS.border}`,
        }}
        onClick={() => handleItemPress()}
      >
        <div className="px-3 py-3 flex flex-row justify-content-between align-items-center">
          <div className="flex justify-content-center align-items-center gap-3">
            <span
              className="flex align-items-center justify-content-center border-circle"
              style={{
                width: 40,
                height: 40,
                backgroundColor: COLORS.brandSoft,
                color: COLORS.brand,
              }}
            >
              <i className="pi pi-sliders-h" style={{ fontSize: "18px" }}></i>
            </span>
            <div>
              <p
                className="text-xl font-bold m-0"
                style={{ color: COLORS.text }}
              >
                Additional Settings
              </p>
              <p className="text-sm m-0" style={{ color: COLORS.muted }}>
                Feature toggles, grouped by area — expand only what you need
              </p>
            </div>
          </div>
          <i
            className={!selectedItem ? "pi pi-angle-down" : "pi pi-angle-up"}
            style={{ fontSize: "22px", color: COLORS.muted }}
          ></i>
        </div>
      </div>

      {selectedItem && (
        <div className="mx-3 mb-3">
          <Accordion multiple>
            {[
              {
                key: "storeManagement",
                title: "Store Management",
                items: generalStoreToggles,
              },
              {
                key: "customerManagement",
                title: "Customer Management",
                items: customerToggles,
              },
              {
                key: "notifications",
                title: "Notifications",
                items: notificationToggles,
              },
              {
                key: "subscriptions",
                title: "Subscriptions Management & Package Carry Forward",
                items: subscriptionToggles,
              },
              {
                key: "orderEdit",
                title: "Order Edit",
                items: orderEditToggles,
              },
              {
                key: "charges",
                title: "Charges & Discounts",
                items: chargesDiscountsToggles,
              },
              {
                key: "delivery",
                title: "Delivery Verification",
                items: deliveryVerificationToggles,
              },
              { key: "rider", title: "Rider", items: riderToggles },
              {
                key: "customerApp",
                title: "Customer App",
                items: customerAppToggles,
              },
            ]
              .map((group) => ({
                ...group,
                visibleItems: group.items.filter((item) => item.show !== false),
                style: CATEGORY_STYLE[group.key],
              }))
              .filter((group) => group.visibleItems.length > 0)
              .map((group) => (
                <AccordionTab
                  key={group.key}
                  header={
                    <CategoryHeader
                      title={group.title}
                      icon={group.style.icon}
                      color={group.style.color}
                      enabledCount={
                        group.visibleItems.filter((i) => input[i.key]).length
                      }
                      total={group.visibleItems.length}
                    />
                  }
                >
                  <ToggleGrid
                    items={group.items}
                    input={input}
                    setInput={setInput}
                    accent={group.style.color}
                  />
                </AccordionTab>
              ))}
          </Accordion>
        </div>
      )}

      <SuccessModal
        setModalVisible={setModalVisible}
        modalVisible={modalVisible}
        status={modalStatus}
        text={modalText}
      />

      <div
        className="sticky bottom-0 flex px-3 py-3"
        style={{
          backgroundColor: COLORS.page,
          borderTop: `1px solid ${COLORS.border}`,
        }}
      >
        <Button
          className="border-round-xl w-full"
          onClick={handleSubmit}
          disabled={isButtonPressed}
          style={{
            backgroundColor: COLORS.brand,
            border: "none",
            padding: "12px",
          }}
        >
          <p
            className="w-full text-xl font-semibold flex justify-content-center align-items-center gap-2 m-0"
            style={{ color: "#fff" }}
          >
            <i className="pi pi-check"></i>
            {id ? "Save Changes" : "Create Store"}
          </p>
        </Button>
      </div>

      <AdminMenu setVisible={setVisible} visible={visible} />
    </div>
  );
};

export default StoreManagementCreate;
